/* ============================================================
   Quote details — the valid-until date, the cover note and line descriptions.

   The app's copy of server/src/quotedetails.js (the server's refuses to sign
   an expired quote, fills the note into the email and freezes all three at
   signing). web/scripts/unit-quotes2.mjs runs both over the same inputs and
   fails on any difference, so the hub can't count a day differently from the
   server that decides whether it can be signed.

     validUntil  "YYYY-MM-DD" — the last day the quote can be accepted, on the
                 business's calendar (settings.timezone). Absent / null = no
                 expiry: every quote made before this, and any the contractor
                 cleared.
     coverNote   the contractor's note, plain text with {first_name} and
                 {company}. The customer document carries it filled in.
     lineDescs   { [lineKey]: "one line" } under a priced line; a price-book
                 product's own `description` fills its line when the quote has
                 none ("" clears it on this quote).

   Imports nothing, so the unit tests can transpile it on its own.
   ============================================================ */

export const VALID_DAYS_DEFAULT = 30;
export const SOON_DAYS = 3;
export const EXTEND_DAYS = 14;
export const REMIND_DAYS_DEFAULT = 3;
export const NOTE_MAX = 1200;
export const DESC_MAX = 160;

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;
const pad = (n: number) => String(n).padStart(2, "0");

export function isYmd(s: any): boolean {
  if (typeof s !== "string") return false;
  const m = YMD.exec(s);
  if (!m) return false;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3];
}
const utcOf = (ymd: string) => { const [y, m, d] = ymd.split("-").map(Number); return Date.UTC(y, m - 1, d); };
const ymdOfUtc = (t: number) => { const d = new Date(t); return d.getUTCFullYear() + "-" + pad(d.getUTCMonth() + 1) + "-" + pad(d.getUTCDate()); };

/* the calendar day at `now` in `tz`; no / unknown zone → this device's calendar */
export function todayIn(tz?: any, now: any = Date.now()): string {
  const t = typeof now === "number" ? now : Date.parse(now);
  const d = new Date(Number.isFinite(t) ? t : Date.now());
  if (tz) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", { timeZone: String(tz), year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d);
      const get = (k: string) => (parts.find((p) => p.type === k) || ({} as any)).value;
      const y = get("year"), m = get("month"), day = get("day");
      if (y && m && day) return `${y}-${m}-${day}`;
    } catch (e) { /* unknown zone → the device calendar */ }
  }
  return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
}

export function addDays(ymd: any, n: any): string | null {
  if (!isYmd(ymd)) return null;
  return ymdOfUtc(utcOf(ymd) + Math.round(Number(n) || 0) * 86400000);
}
export function daysBetween(a: any, b: any): number | null {
  if (!isYmd(a) || !isYmd(b)) return null;
  return Math.round((utcOf(b) - utcOf(a)) / 86400000);
}

export function validDaysOf(settings: any): number {
  const v = settings ? settings.quoteValidDays : undefined;
  if (v == null || v === "") return VALID_DAYS_DEFAULT;
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? Math.max(0, Math.min(365, n)) : VALID_DAYS_DEFAULT;
}
export function defaultValidUntil(settings: any, createdAt: any = Date.now(), tz: any = null): string | null {
  const days = validDaysOf(settings);
  return days > 0 ? addDays(todayIn(tz, createdAt), days) : null;
}

export type Expiry = { validUntil: string | null; today: string | null; daysLeft: number | null; expired: boolean; soon: boolean; state: "none" | "valid" | "soon" | "expired" };
export function expiryOf(validUntil: any, { now = Date.now(), tz = null, soonDays = SOON_DAYS }: { now?: any; tz?: any; soonDays?: number } = {}): Expiry {
  if (!isYmd(validUntil)) return { validUntil: null, today: null, daysLeft: null, expired: false, soon: false, state: "none" };
  const today = todayIn(tz, now);
  const daysLeft = daysBetween(today, validUntil) as number;
  const expired = daysLeft < 0;
  const soon = !expired && daysLeft <= soonDays;
  return { validUntil, today, daysLeft, expired, soon, state: expired ? "expired" : soon ? "soon" : "valid" };
}
export function extendValidUntil(validUntil: any, days: number = EXTEND_DAYS, { now = Date.now(), tz = null }: { now?: any; tz?: any } = {}): string | null {
  const today = todayIn(tz, now);
  const from = isYmd(validUntil) && validUntil > today ? validUntil : today;
  return addDays(from, days);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function fmtDate(ymd: any, today: any = null): string {
  if (!isYmd(ymd)) return "";
  const [y, m, d] = String(ymd).split("-").map(Number);
  const sameYear = isYmd(today) && String(today).slice(0, 4) === String(ymd).slice(0, 4);
  return MONTHS[m - 1] + " " + d + (sameYear ? "" : ", " + y);
}
export function expiryPhrase(ex: Expiry | null): string {
  if (!ex || ex.state === "none") return "";
  if (ex.expired) return "expired " + fmtDate(ex.validUntil, ex.today);
  if (ex.daysLeft === 0) return "expires today";
  if (ex.daysLeft === 1) return "expires tomorrow";
  return "expires in " + ex.daysLeft + " days";
}
/* "Valid until Oct 25 · expires in 12 days" — the line the homeowner reads */
export function validityLine(ex: Expiry | null): string {
  if (!ex || ex.state === "none") return "";
  if (ex.expired) return "Expired on " + fmtDate(ex.validUntil, ex.today);
  return "Valid until " + fmtDate(ex.validUntil, ex.today) + " · " + expiryPhrase(ex);
}

export function firstNameOf(name: any): string {
  const w = String(name == null ? "" : name).trim().split(/\s+/)[0] || "";
  return /^[&+,/]/.test(w) ? "" : w.replace(/[,;:.]+$/, "");
}

const CONTROL = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;
export function renderCoverNote(template: any, { firstName = "", company = "" }: { firstName?: any; company?: any } = {}): string {
  let t = String(template == null ? "" : template).replace(/\r\n?/g, "\n").replace(CONTROL, "");
  t = t.replace(/\{\s*(first[ _]?name|company)\s*\}/gi, (_: string, k: string) =>
    (/^company$/i.test(k) ? (String(company || "").trim() || "our team") : (String(firstName || "").trim() || "there")));
  t = t.split("\n").map((l) => l.replace(/[ \t]+$/, "")).join("\n").replace(/\n{3,}/g, "\n\n").trim();
  return t.slice(0, NOTE_MAX);
}
export function cleanNote(template: any): string {
  const t = String(template == null ? "" : template).replace(/\r\n?/g, "\n").replace(CONTROL, "");
  return t.split("\n").map((l) => l.replace(/[ \t]+$/, "")).join("\n").replace(/\n{3,}/g, "\n\n").trim().slice(0, NOTE_MAX);
}
export function cleanDesc(s: any): string {
  return String(s == null ? "" : s).replace(/[\u0000-\u001F\u007F]+/g, " ").replace(/\s+/g, " ").trim().slice(0, DESC_MAX);
}

export const signedOf = (q: any) => !!(q && ((q.signature && q.signature.at) || q.signedTotal != null || q.status === "signed" || q.status === "paid"));
const docOf = (d: any) => (d && typeof d === "object" ? d : null);

/* the note the customer is shown: frozen when signed, else the stamped
   document (all a real hub link gets), else the template */
export function shownCoverNote(q: any, { firstName, company }: { firstName?: any; company?: any } = {}): string {
  if (!q) return "";
  const sd = signedOf(q) ? docOf(q.signedDoc) : null;
  if (sd && typeof sd.coverNote === "string") return sd.coverNote;
  const cd = docOf(q.customerDoc);
  if (cd && typeof cd.coverNote === "string") return cd.coverNote;
  if (typeof q.coverNote === "string") return renderCoverNote(q.coverNote, { firstName: firstName != null ? firstName : firstNameOf(q.customer), company });
  return "";
}
export function shownValidUntil(q: any): string | null {
  if (!q) return null;
  const sd = signedOf(q) ? docOf(q.signedDoc) : null;
  if (sd && Object.prototype.hasOwnProperty.call(sd, "validUntil")) return isYmd(sd.validUntil) ? sd.validUntil : null;
  if (isYmd(q.validUntil)) return q.validUntil;
  const cd = docOf(q.customerDoc);
  return cd && isYmd(cd.validUntil) ? cd.validUntil : null;
}

/* what the customer document gets stamped with: the frozen note once signed,
   else the template filled in fresh (the document is re-stamped on every
   edit, so it never lags the template) */
export function liveCoverNote(q: any, { firstName, company }: { firstName?: any; company?: any } = {}): string {
  if (!q) return "";
  const sd = signedOf(q) ? docOf(q.signedDoc) : null;
  if (sd && typeof sd.coverNote === "string") return sd.coverNote;
  return typeof q.coverNote === "string" ? renderCoverNote(q.coverNote, { firstName: firstName != null ? firstName : firstNameOf(q.customer), company }) : "";
}

export function expiredSignMessage(validUntil: any, { method = "hub", company = "" }: { method?: string; company?: any } = {}): string {
  const on = fmtDate(validUntil);
  if (method === "in_person") return `This estimate expired on ${on}. Extend it on the estimate first, then sign — nothing was signed.`;
  return `This quote expired on ${on}. Ask ${String(company || "").trim() || "the contractor"} to refresh it — nothing was signed.`;
}

/* ---- app-side helpers ---- */

/* the business's zone (the server judges expiry there); null → this device */
export const businessTz = (settings: any) => (settings && (settings.timezone || settings.tz)) || null;

/* A NEW quote picks up the defaults from Settings → Quoting: its valid-until
   date and the default cover note (kept as the template; merge fields fill in
   when it's shown). A quote that already says either keeps what it says. */
export function withQuoteDefaults(q: any, settings: any, now: any = Date.now()): any {
  if (!q) return q;
  const out: any = { ...q };
  if (!Object.prototype.hasOwnProperty.call(out, "validUntil")) {
    const v = defaultValidUntil(settings, out.createdAt || now, businessTz(settings));
    if (v) out.validUntil = v;
  }
  if (!Object.prototype.hasOwnProperty.call(out, "coverNote")) {
    const t = cleanNote(settings && settings.quoteCoverNote);
    if (t) out.coverNote = t;
  }
  // the service's own deposit default (Settings → Payments & tax, per service;
  // lib/verticals.ts serviceDefaultsOf — read inline so this file stays
  // import-free for the unit harness) is stamped as the quote's own percentage,
  // so every reader — the terms box, the hub's pay card, the server's offered
  // amounts — agrees. 0 = nothing up front, paid on completion. Only when the
  // quote doesn't already say, and never for a fence quote with no default set.
  // …or per job type under the service (lib/verticals.ts depositPctFor): a
  // lift is paid on completion while a coating takes a deposit; a mixed quote
  // takes the largest
  if (out.depositPct == null && out.depositAmt == null) {
    const v = out.vertical === "concrete" ? "concrete" : "fence";
    const all = settings && settings.serviceDefaults && typeof settings.serviceDefaults === "object" ? settings.serviceDefaults : {};
    const sd = all[v] && typeof all[v] === "object" ? all[v] : {};
    const n = (x: any) => (x == null || x === "" || !isFinite(Number(x)) ? null : Math.max(0, Math.min(100, Number(x))));
    const by = sd.depositByJobType && typeof sd.depositByJobType === "object" ? sd.depositByJobType : null;
    let best: number | null = null;
    if (by && Array.isArray(out.jobTypes)) out.jobTypes.forEach((jt: any) => { const x = n(by[jt]); if (x != null) best = best == null ? x : Math.max(best, x); });
    const d = best != null ? best : n(sd.depositPct);
    if (d != null) out.depositPct = d;
  }
  return out;
}

/* Is this quote expired for its customer right now? Only an OPEN quote can be:
   a signed one was accepted in time, a lost one is out of play. */
export function openExpiry(q: any, settings: any, now: any = Date.now()): Expiry {
  const none = expiryOf(null);
  if (!q || signedOf(q) || q.status === "lost" || q.archivedAt) return none;
  return expiryOf(q.validUntil, { now, tz: businessTz(settings) });
}
